// src/pages/admin/Pricing.js
import React, { useState } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel, // Ensure this is imported
  Checkbox, // Make sure Checkbox is imported too
  useTheme,
  alpha
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CleaningServices as CleaningIcon,
  AttachMoney as MoneyIcon,
  Schedule as DurationIcon,
  Category as CategoryIcon,
  MoreVert as MoreIcon,
} from "@mui/icons-material";

// Mock data for pricing
const mockServices = [
  {
    id: 1,
    name: "Standard Cleaning",
    description: "Basic cleaning service for regular maintenance",
    basePrice: 89,
    hourlyRate: 30,
    duration: "2-3 hours",
    category: "Residential",
    rooms: ["Living Room", "Kitchen", "Bathroom", "Bedroom"],
    status: true,
    popular: true
  },
  {
    id: 2,
    name: "Deep Cleaning",
    description: "Thorough cleaning for move-in/move-out or seasonal cleaning",
    basePrice: 189,
    hourlyRate: 35,
    duration: "4-6 hours",
    category: "Residential",
    rooms: ["All Rooms", "Inside Cabinets", "Windows", "Baseboards"],
    status: true,
    popular: false
  },
  {
    id: 3,
    name: "Office Cleaning",
    description: "Commercial cleaning for offices and workspaces",
    basePrice: 149,
    hourlyRate: 40,
    duration: "3-4 hours",
    category: "Commercial",
    rooms: ["Reception", "Offices", "Kitchenette", "Restrooms"],
    status: true,
    popular: true
  },
  {
    id: 4,
    name: "Move-In/Move-Out Cleaning",
    description: "Comprehensive cleaning for property transitions",
    basePrice: 249,
    hourlyRate: 45,
    duration: "5-7 hours",
    category: "Residential",
    rooms: ["All Rooms", "Appliances", "Cabinets", "Walls"],
    status: true,
    popular: false
  },
  {
    id: 5,
    name: "Carpet Cleaning",
    description: "Specialized carpet and upholstery cleaning",
    basePrice: 99,
    hourlyRate: 50,
    duration: "2-3 hours",
    category: "Specialty",
    rooms: ["Carpet Areas", "Upholstery", "Rugs"],
    status: false,
    popular: false
  }
];

const roomOptions = [
  "Living Room", "Kitchen", "Bathroom", "Bedroom", "Dining Room", 
  "Home Office", "Garage", "Basement", "All Rooms", "Inside Cabinets",
  "Windows", "Baseboards", "Appliances", "Walls", "Carpet Areas",
  "Upholstery", "Rugs", "Reception", "Offices", "Kitchenette", "Restrooms"
];

export default function Pricing() {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [newService, setNewService] = useState({
    name: "",
    description: "",
    basePrice: "",
    hourlyRate: "",
    duration: "",
    category: "Residential",
    rooms: [],
    status: true,
    popular: false
  });

  const filteredServices = mockServices.filter(service =>
    service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    service.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    service.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenDialog = (service = null) => {
    if (service) {
      setEditingService(service);
      setNewService({ ...service });
    } else {
      setEditingService(null);
      setNewService({
        name: "",
        description: "",
        basePrice: "",
        hourlyRate: "",
        duration: "",
        category: "Residential",
        rooms: [],
        status: true,
        popular: false
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingService(null);
  };

  const handleSaveService = () => {
    // Here you would save to your backend
    console.log('Saving service:', newService);
    handleCloseDialog();
  };

  const handleRoomToggle = (room) => {
    setNewService(prev => ({
      ...prev,
      rooms: prev.rooms.includes(room)
        ? prev.rooms.filter(r => r !== room)
        : [...prev.rooms, room]
    }));
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Residential': return 'primary';
      case 'Commercial': return 'secondary';
      case 'Specialty': return 'warning';
      default: return 'default';
    }
  };

  const stats = [
    { label: 'Active Services', value: '8', change: '+2' },
    { label: 'Categories', value: '3', change: '0' },
    { label: 'Avg. Base Price', value: '$142', change: '+5%' },
    { label: 'Popular Services', value: '3', change: '+1' },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            Service Pricing
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Service
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage service types, pricing, and availability
        </Typography>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {stat.label}
                </Typography>
                <Chip 
                  label={stat.change} 
                  size="small" 
                  color="success"
                  variant="outlined"
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search services..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value="all"
                label="Category"
                onChange={() => {}} // Add filter logic
              >
                <MenuItem value="all">All Categories</MenuItem>
                <MenuItem value="residential">Residential</MenuItem>
                <MenuItem value="commercial">Commercial</MenuItem>
                <MenuItem value="specialty">Specialty</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value="all"
                label="Status"
                onChange={() => {}} // Add filter logic
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Services Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
              <TableRow>
                <TableCell>Service</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Base Price</TableCell>
                <TableCell>Hourly Rate</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Rooms</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Popular</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredServices.map((service) => (
                <TableRow key={service.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CleaningIcon color="primary" />
                      <Box>
                        <Typography variant="subtitle1" fontWeight="600">
                          {service.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {service.description}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={service.category} 
                      color={getCategoryColor(service.category)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <MoneyIcon color="primary" fontSize="small" />
                      <Typography fontWeight="600" color="primary">
                        ${service.basePrice}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight="600">
                      ${service.hourlyRate}/hr
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <DurationIcon color="action" fontSize="small" />
                      <Typography>{service.duration}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 200 }}>
                      {service.rooms.slice(0, 2).map(room => (
                        <Chip key={room} label={room} size="small" variant="outlined" />
                      ))}
                      {service.rooms.length > 2 && (
                        <Chip 
                          label={`+${service.rooms.length - 2}`} 
                          size="small" 
                          color="primary"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Switch 
                      checked={service.status} 
                      color="success"
                      onChange={() => {}} // Add toggle logic
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={service.popular ? "Popular" : "Standard"} 
                      color={service.popular ? "warning" : "default"}
                      size="small"
                      variant={service.popular ? "filled" : "outlined"}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton 
                      size="small" 
                      onClick={() => handleOpenDialog(service)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Add/Edit Service Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingService ? 'Edit Service' : 'Add New Service'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Service Name"
                  value={newService.name}
                  onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={newService.category}
                    label="Category"
                    onChange={(e) => setNewService({ ...newService, category: e.target.value })}
                  >
                    <MenuItem value="Residential">Residential</MenuItem>
                    <MenuItem value="Commercial">Commercial</MenuItem>
                    <MenuItem value="Specialty">Specialty</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <TextField
              label="Description"
              value={newService.description}
              onChange={(e) => setNewService({ ...newService, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Base Price"
                  type="number"
                  value={newService.basePrice}
                  onChange={(e) => setNewService({ ...newService, basePrice: e.target.value })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Hourly Rate"
                  type="number"
                  value={newService.hourlyRate}
                  onChange={(e) => setNewService({ ...newService, hourlyRate: e.target.value })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    endAdornment: <InputAdornment position="end">/hr</InputAdornment>,
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Duration"
                  value={newService.duration}
                  onChange={(e) => setNewService({ ...newService, duration: e.target.value })}
                  placeholder="e.g., 2-3 hours"
                  fullWidth
                />
              </Grid>
            </Grid>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Included Rooms/Areas
              </Typography>
              <FormGroup sx={{ display: 'flex', flexDirection: 'row', gap: 1, flexWrap: 'wrap' }}>
                {roomOptions.map(room => (
                  <FormControlLabel
                    key={room}
                    control={
                      <Checkbox
                        checked={newService.rooms.includes(room)}
                        onChange={() => handleRoomToggle(room)}
                      />
                    }
                    label={room}
                  />
                ))}
              </FormGroup>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newService.status}
                      onChange={(e) => setNewService({ ...newService, status: e.target.checked })}
                      color="success"
                    />
                  }
                  label="Active Service"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newService.popular}
                      onChange={(e) => setNewService({ ...newService, popular: e.target.checked })}
                      color="warning"
                    />
                  }
                  label="Mark as Popular"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveService} variant="contained">
            {editingService ? 'Update Service' : 'Create Service'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}